import { useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Check } from "lucide-react"

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

interface AccountTabProps {
  email: string
}

export function AccountTab({ email: initialEmail }: AccountTabProps) {
  const [email, setEmail] = useState(initialEmail)
  const [emailSaving, setEmailSaving] = useState(false)
  const [emailErrors, setEmailErrors] = useState<Record<string, string[]>>({})
  const [emailSuccess, setEmailSuccess] = useState(false)

  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [passwordSaving, setPasswordSaving] = useState(false)
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string[]>>({})
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword

  async function handleEmailUpdate() {
    setEmailSaving(true)
    setEmailErrors({})
    setEmailSuccess(false)

    try {
      const res = await fetch("/api/account/email", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCSRFToken(),
        },
        body: JSON.stringify({ email_address: email }),
      })

      if (res.ok) {
        setEmailSuccess(true)
        setTimeout(() => setEmailSuccess(false), 3000)
      } else {
        const data = await res.json()
        setEmailErrors(data.errors || {})
      }
    } finally {
      setEmailSaving(false)
    }
  }

  async function handlePasswordUpdate() {
    setPasswordSaving(true)
    setPasswordErrors({})
    setPasswordSuccess(false)

    try {
      const res = await fetch("/api/account/password", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCSRFToken(),
        },
        body: JSON.stringify({
          current_password: currentPassword,
          password: newPassword,
          password_confirmation: confirmPassword,
        }),
      })

      if (res.ok) {
        setPasswordSuccess(true)
        setCurrentPassword("")
        setNewPassword("")
        setConfirmPassword("")
        setTimeout(() => setPasswordSuccess(false), 3000)
      } else {
        const data = await res.json()
        setPasswordErrors(data.errors || {})
      }
    } finally {
      setPasswordSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold">Email Address</h3>
        <Separator className="my-4" />

        <div className="space-y-3">
          <div>
            <Label>Email</Label>
            <Input
              className="mt-1"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {emailErrors.email_address && (
              <p className="mt-1 text-sm text-destructive">{emailErrors.email_address.join(", ")}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={handleEmailUpdate} disabled={emailSaving || email === initialEmail}>
              {emailSaving ? "Saving..." : "Update Email"}
            </Button>
            {emailSuccess && (
              <span className="flex items-center gap-1 text-sm text-emerald-600 dark:text-emerald-400">
                <Check className="h-4 w-4" /> Updated
              </span>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold">Change Password</h3>
        <Separator className="my-4" />

        <div className="space-y-3">
          <div>
            <Label>Current Password</Label>
            <Input
              className="mt-1"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            {passwordErrors.current_password && (
              <p className="mt-1 text-sm text-destructive">{passwordErrors.current_password.join(", ")}</p>
            )}
          </div>

          <div>
            <Label>New Password</Label>
            <Input
              className="mt-1"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            {passwordErrors.password && (
              <p className="mt-1 text-sm text-destructive">{passwordErrors.password.join(", ")}</p>
            )}
          </div>

          <div>
            <Label>Confirm New Password</Label>
            <Input
              className="mt-1"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            {confirmPassword.length > 0 && (
              <p className={`mt-1 text-sm ${passwordsMatch ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                {passwordsMatch ? "Passwords match" : "Passwords do not match"}
              </p>
            )}
            {passwordErrors.password_confirmation && (
              <p className="mt-1 text-sm text-destructive">{passwordErrors.password_confirmation.join(", ")}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={handlePasswordUpdate}
              disabled={passwordSaving || !currentPassword || !passwordsMatch}
            >
              {passwordSaving ? "Saving..." : "Change Password"}
            </Button>
            {passwordSuccess && (
              <span className="flex items-center gap-1 text-sm text-emerald-600 dark:text-emerald-400">
                <Check className="h-4 w-4" /> Password changed
              </span>
            )}
          </div>
        </div>
      </Card>
    </div>
  )
}
